import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { ReactElement, useCallback } from 'react';

import type { ItemSummary } from '@/types/item/ItemSummary';

import PageTitle from '@/components/PageTitle/PageTitle';
import { Catalog } from '@/types/Catalog';
import { ReviewResponse } from '@/types/Review';
import { shortFormatDate } from '@/utils/dates';
import { getImageUrl, GetImageUrlParams, getItemUrlFromSlug } from '@/utils/urls';

import ReviewedPageHeaderMeta from './ReviewedPageHeaderMeta';

type Props = {
    baseUrl: string;
    catalog: Catalog;
    completedReview: ReviewResponse;
    items: ItemSummary[];
};

export const ReviewedPageHeader = ({ baseUrl, catalog, completedReview, items }: Props) => {
    const mapImages = useCallback(
        ({ catalogId, imageVersion, itemId, photos, sellerId, slugWithLocation, title }: ItemSummary): ReactElement => {
            const imageOptions: GetImageUrlParams = {
                catalogId,
                height: 240,
                imageNumber: photos?.[0],
                imageVersion,
                itemId,
                sellerId,
            };

            const url = getImageUrl(imageOptions);
            const itemUrl = getItemUrlFromSlug(itemId, slugWithLocation);
            return (
                <div
                    className="relative mr-xs h-[120px] w-[120px] border border-border-secondary"
                    key={itemId}
                >
                    <Link
                        href={itemUrl}
                        key={`${itemId}-header-image`}
                        rel="noopener"
                        target="_blank"
                    >
                        <img
                            alt={title}
                            className="absolute top-0 right-0 bottom-0 left-0 m-auto h-auto max-h-full w-auto max-w-full"
                            src={url}
                        />
                    </Link>
                </div>
            );
        },
        []
    );

    const { saleStartTs, sellerName: name, title } = catalog;
    const startDate = shortFormatDate(saleStartTs);

    return (
        <div>
            <ReviewedPageHeaderMeta baseUrl={baseUrl} />
            <PageTitle
                className="text-center"
                title={<FormattedMessage id="review_page.review_your_auction" />}
            />
            <div>
                <H2>{name}</H2>
                <BodyPrimary>{title}</BodyPrimary>
                <br />
                {startDate}
            </div>
            <div className="mt-sm flex">{items.map(mapImages)}</div>
            <br />
            <BodyPrimary>
                <FormattedMessage
                    id="review_page.rated_date"
                    values={{
                        date: shortFormatDate(completedReview.ReviewSubmittedTS),
                        name: <b key="review-page-rated-date-name">{name}</b>,
                    }}
                />
            </BodyPrimary>
        </div>
    );
};
